import React, {useState, useRef, useEffect} from 'react';
import {theme} from '@styles/theme';
import Text from '@atoms/Text';
import {ChevronSort, Close, Search} from '@carbon/icons-react';
import {
  Table,
  TableBody,
  TableContainer,
  TableHeader,
  TableRow,
  HeaderContent,
  SortIcon,
} from './ModulesTable.styles';
import {
  ClearButton,
  HeaderTextWithIcon,
  SearchInputContainer,
  StyledSearchInput,
  TableCell,
  TableHeaderCell,
  TableStatusWrapper,
  UserNameBlockWrapper,
  UserNameWrapper,
} from './UsersTable.styles';
import Checkbox from '@atoms/Checkbox';
import CircleButton from '@atoms/CircleButton';
import {IRole, IUser} from '@types';
import StatusLabel from '@atoms/StatusLabel';
import {EUserStatuses} from '@constants/usersStatuses';
import Avatar from '@atoms/Avatar';
import {useSelector} from 'react-redux';
import {system_roles} from '@store/slices/users';
import {IDLE, PENDING} from '@constants/loadingState';
import Skeleton from 'react-loading-skeleton';
import {formatLastUpdate} from '@utils/common';
import {RootState} from '@store';
import {EUserRoles} from '@constants/usersRoles';

interface UsersTableProps {
  users: IUser[];
  onSort: (field: string) => void;
  onSelectRow?: (rowsIds: string[]) => void;
  onDelete: (user_id: number) => void;
  onEdit: (user_id: number) => void;
  onSendReminder: (user_id: number) => void;
  loading: string;
}

const UsersTable: React.FC<UsersTableProps> = ({
  users,
  onSort,
  onSelectRow,
  onDelete,
  onEdit,
  onSendReminder,
  loading,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [showSearchInput, setShowSearchInput] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const systemRolesArr: IRole[] = useSelector(system_roles);
  const currentUserSystemRole = useSelector(
    (state: RootState) => state.profile.profile?.role_id,
  );

  const filteredUsers = users.filter(
    user =>
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  useEffect(() => {
    if (onSelectRow) onSelectRow(selectedUsers);
  }, [selectedUsers]);

  const allSelected = users.length > 0 && selectedUsers.length === users.length;

  const handleSelectAll = (e: any) => {
    if (e.target.checked) {
      const allIds = users.map(user => String(user.id));
      setSelectedUsers(allIds);
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (userId: string, e: any) => {
    if (e.target.checked) {
      setSelectedUsers(prev => [...prev, userId]);
    } else {
      setSelectedUsers(prev => prev.filter(id => id !== userId));
    }
  };

  // Обработчик клика по ячейке названия теста
  const handleHeaderClick = () => {
    setShowSearchInput(true);
  };

  // Очистка поиска и скрытие инпута
  const handleClearSearch = () => {
    setSearchTerm('');
    setShowSearchInput(false);
  };

  // Обработчик клика вне инпута
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node) &&
        searchTerm === ''
      ) {
        setShowSearchInput(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [searchTerm]);

  // Фокус на инпут при его появлении
  useEffect(() => {
    if (showSearchInput && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearchInput]);

  return (
    <TableContainer>
      <Table>
        <TableHeader>
          <TableRow>
            {/* Checkbox column */}
            <TableHeaderCell>
              <Checkbox
                id={'select-all-users'}
                checked={allSelected}
                onChange={handleSelectAll}
              />
            </TableHeaderCell>

            {/* User Name column */}
            <TableHeaderCell onClick={handleHeaderClick} style={{cursor: 'pointer'}}>
              {showSearchInput ? (
                <SearchInputContainer ref={searchContainerRef}>
                  <StyledSearchInput
                    ref={searchInputRef}
                    type="text"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <ClearButton onClick={handleClearSearch}>
                      <Close size={12} />
                    </ClearButton>
                  )}
                </SearchInputContainer>
              ) : (
                <HeaderContent>
                  <HeaderTextWithIcon>
                    <Search size={16} />
                    <Text text="Имя и логин" type="tinyM" />
                  </HeaderTextWithIcon>
                  <SortIcon onClick={() => onSort('name')}>
                    <ChevronSort name="sort" size={12} color={theme.colors.basic02} />
                  </SortIcon>
                </HeaderContent>
              )}
            </TableHeaderCell>

            {/* Integrations/Modules count column */}
            <TableHeaderCell>
              <HeaderContent>
                <Text text="Роль/Тип пользователя" type="tinyM" />
                <SortIcon onClick={() => onSort('count')}>
                  <ChevronSort name="sort" size={12} color={theme.colors.basic02} />
                </SortIcon>
              </HeaderContent>
            </TableHeaderCell>

            {/* Automated column */}
            <TableHeaderCell>
              <HeaderContent>
                <Text text="Статус" type="tinyM" />
                <SortIcon onClick={() => onSort('automated')}>
                  <ChevronSort name="sort" size={12} color={theme.colors.basic02} />
                </SortIcon>
              </HeaderContent>
            </TableHeaderCell>

            {/* Last Activity column */}
            <TableHeaderCell>
              <HeaderContent>
                <Text text="Последняя активность" type="tinyM" />
                <SortIcon onClick={() => onSort('lastActivity')}>
                  <ChevronSort name="sort" size={12} color={theme.colors.basic02} />
                </SortIcon>
              </HeaderContent>
            </TableHeaderCell>

            {/* Actions column (empty header) */}
            <TableHeaderCell>{/* Empty header for actions */}</TableHeaderCell>
          </TableRow>
        </TableHeader>

        <TableBody>
          {loading === IDLE &&
            filteredUsers.map(user => (
              <TableRow key={user.id}>
                {/* Checkbox cell */}
                <TableCell>
                  {(currentUserSystemRole !== EUserRoles.SPACE_ADMIN ||
                    (currentUserSystemRole === EUserRoles.SPACE_ADMIN &&
                      user.role_id !== EUserRoles.TECH_ADMIN)) && (
                    <Checkbox
                      id={`select-user-id-${user.id}`}
                      checked={selectedUsers.includes(String(user.id))}
                      onChange={selected => handleSelectUser(String(user.id), selected)}
                    />
                  )}
                </TableCell>

                {/* User Name cell */}
                <TableCell>
                  <UserNameBlockWrapper>
                    <Avatar img={user.avatar_url} />
                    <UserNameWrapper>
                      <Text
                        text={user.username}
                        type="tinyM"
                        color={theme.colors.basic01}
                      />
                      <Text text={user.email} type="tinyR" color={theme.colors.basic02} />
                    </UserNameWrapper>
                  </UserNameBlockWrapper>
                </TableCell>

                {/* Role cell */}
                <TableCell>
                  <Text
                    text={
                      systemRolesArr.find(role => {
                        return user.role_id == role.id;
                      })?.name || '-'
                    }
                    type="tinyR"
                  />
                </TableCell>

                {/* Status cell */}
                <TableCell>
                  <TableStatusWrapper>
                    <StatusLabel
                      text={
                        EUserStatuses.ACTIVE == user.status_id ? 'активен' : 'в ожидании'
                      }
                      type={EUserStatuses.ACTIVE == user.status_id ? 'green' : 'yellow'}
                    />
                  </TableStatusWrapper>
                </TableCell>

                {/* Last Activity cell */}
                <TableCell>
                  <Text text={formatLastUpdate(user.lastActivity || '')} type="tinyR" />
                </TableCell>

                {/* Actions cell */}
                <TableCell>
                  {(currentUserSystemRole !== EUserRoles.SPACE_ADMIN ||
                    (currentUserSystemRole === EUserRoles.SPACE_ADMIN &&
                      user.role_id !== EUserRoles.TECH_ADMIN)) && (
                    <CircleButton
                      key={`table-user-menu-${user.id}`}
                      menu={[
                        {title: 'Редактировать', onClick: () => onEdit(user.id)},
                        {
                          title: 'Выслать напоминание',
                          onClick: () => onSendReminder(user.id),
                        },
                        {
                          title: 'Удалить',
                          onClick: () => onDelete(user.id),
                          color: theme.colors.signalRed00,
                        },
                      ]}
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          {loading === PENDING &&
            [...Array(4)].map(i => (
              <TableRow key={`test-table-row-${i}`}>
                <TableCell>
                  <Skeleton width={18} height={18} />
                </TableCell>
                <TableCell>
                  <UserNameBlockWrapper>
                    <Skeleton circle width={32} height={32} />
                    <Skeleton width={176} height={32} />
                  </UserNameBlockWrapper>
                </TableCell>
                <TableCell>
                  <Skeleton width={146} height={16} />
                </TableCell>
                <TableCell>
                  <Skeleton width={89} height={24} borderRadius={theme.roundedBig} />
                </TableCell>
                <TableCell>
                  <Skeleton width={174} height={16} />
                </TableCell>
                <TableCell>
                  <Skeleton circle width={32} height={32} />
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default UsersTable;
